import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { ChevronDownIcon, CircleMinusIcon } from 'lucide-react'
import React from 'react'

interface EnumSelectBaseProps<T extends string | number | null> {
  options: T[]
  label?: string
  placeholder?: string
  itemFormatter: (opt: T) => React.ReactNode
  dropdownMenuContentProps?: React.ComponentProps<typeof DropdownMenuContent>
  disabled?: boolean
  required?: boolean
  className?: React.ComponentProps<'button'>['className']
  hideCount?: boolean
  hideSelectAll?: boolean
}

export type EnumSelectProps<T extends string | number> =
  React.PropsWithChildren<EnumSelectBaseProps<T>> &
    (
      | {
          single: true
          value: T | null
          onChange: (value: T | null) => void
        }
      | {
          single?: false
          value: T[] | null
          onChange: (value: T[] | null) => void
        }
    )

export default function EnumSelect<T extends string | number>({
  value,
  onChange,
  single,
  placeholder,
  label = 'Select',
  options,
  itemFormatter,
  dropdownMenuContentProps,
  required,
  children,
  className,
  hideCount,
  hideSelectAll,
  ...props
}: EnumSelectProps<T> &
  Omit<React.ComponentProps<'button'>, keyof EnumSelectProps<T>>) {
  const canSelectAll =
    !single && (value === null || value.length < options.length)
  const selectAll = () => !single && onChange([...options])
  const selectNone = () => onChange(null)

  const handleCheckedChange = (checked: boolean, status: T) => {
    if (single) return

    // Checking
    if (checked) {
      const newVal = [...(value || []), status]
      return onChange(newVal)
    }

    // Remove from array
    const newVal = (value || []).filter((s) => s !== status)
    // If none selected, store as null
    if (newVal.length === 0) return onChange(null)
    onChange(newVal)
  }

  const handleSelect = (v: T | null) => {
    if (single) onChange(v)
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {children || (
          <Button
            variant="outline"
            className={cn(
              'justify-between font-normal',
              value === null || (Array.isArray(value) && value.length === 0)
                ? 'text-muted-foreground'
                : null,
              className
            )}
            {...props}
          >
            {!single && value !== null && value.length > 0 && !hideCount && (
              <Badge className="rounded-full p-0 px-1 text-xs tabular-nums">
                {value.length}
              </Badge>
            )}
            {single && value !== null && !Array.isArray(value)
              ? itemFormatter?.(value) || value
              : placeholder || label}
            <ChevronDownIcon className="size-4" />
          </Button>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" {...dropdownMenuContentProps}>
        <DropdownMenuLabel className="flex items-center justify-between gap-3">
          {label}
          {!single && !hideSelectAll && (
            <div className="w-24 text-right">
              <Button
                variant="ghost"
                size="xs"
                className="text-muted-foreground"
                onClick={canSelectAll ? selectAll : selectNone}
              >
                Select {canSelectAll ? 'All' : 'None'}
              </Button>
            </div>
          )}
          {/* Unassign */}
          {single && value !== null && !required && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="xs"
                  onClick={() => handleSelect(null)}
                  aria-label={`Remove ${label}`}
                >
                  <CircleMinusIcon className="text-destructive" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Remove {label}</TooltipContent>
            </Tooltip>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />

        {options.map((opt) =>
          !single ? (
            <DropdownMenuCheckboxItem
              key={opt}
              checked={value !== null && value.includes(opt)}
              onCheckedChange={(v) => handleCheckedChange(v, opt)}
              onSelect={(e) => e.preventDefault()}
            >
              {itemFormatter?.(opt) || opt}
            </DropdownMenuCheckboxItem>
          ) : (
            <DropdownMenuItem
              key={opt}
              onClick={() => handleSelect(opt)}
              className={value === opt ? 'bg-accent text-primary' : ''}
            >
              {itemFormatter?.(opt) || opt}
            </DropdownMenuItem>
          )
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
